/**
 * ProofVerifyPage — `/proof/verify`. Public, no auth, no shell.
 *
 * Anyone — investor, journalist, regulator, customer — can paste a proof JSON
 * blob (from GET /api/v1/proof/{seq}) and have the server verify the ed25519
 * signature server-side. The result is clearly marked VALID or INVALID with
 * the signing pubkey so the user can cross-reference it against /omega/state.
 */

import { useState } from 'react';
import { apiBaseUrl } from '@/api/http';
import { GiOrbitLogo } from '@/components/GiOrbitLogo';

type VerifyState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; valid: boolean; signedBy: string; existsInLog: boolean; logSeq: number | null }
  | { kind: 'error'; detail: string };

async function callVerify(jsonText: string): Promise<{
  valid: boolean;
  signed_by: string;
  exists_in_log: boolean;
  log_seq: number | null;
}> {
  const parsed = JSON.parse(jsonText);
  const url = `${apiBaseUrl()}/api/v1/proof/verify`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(parsed),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.detail ?? body.title ?? `HTTP ${resp.status}`);
  }
  return resp.json();
}

export function ProofVerifyPage() {
  const [draft, setDraft] = useState('');
  const [state, setState] = useState<VerifyState>({ kind: 'idle' });

  async function handleVerify() {
    setState({ kind: 'loading' });
    try {
      const result = await callVerify(draft);
      setState({
        kind: 'ok',
        valid: result.valid,
        signedBy: result.signed_by,
        existsInLog: result.exists_in_log,
        logSeq: result.log_seq ?? null,
      });
    } catch (e) {
      setState({ kind: 'error', detail: String(e) });
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', color: '#e5e5e5', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          <GiOrbitLogo size="md" />
          <span style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>GI Engine — Proof Verifier</span>
        </div>

        <p style={{ color: '#9ca3af', marginBottom: 24, lineHeight: 1.6 }}>
          Paste a signed log entry (from{' '}
          <code style={{ background: '#1f1f1f', padding: '2px 6px', borderRadius: 4 }}>
            GET /api/v1/proof/&#123;seq&#125;
          </code>
          ) below to verify its ed25519 signature server-side.
        </p>

        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder='{ "sequence": 42, "entry_type": "brain_execution", ... }'
          rows={12}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: 8,
            color: '#e5e5e5',
            fontFamily: 'monospace',
            fontSize: 13,
            padding: 12,
            resize: 'vertical',
            outline: 'none',
          }}
        />

        <button
          onClick={handleVerify}
          disabled={!draft.trim() || state.kind === 'loading'}
          style={{
            marginTop: 16,
            padding: '10px 28px',
            background: '#6366f1',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 15,
            fontWeight: 600,
            cursor: draft.trim() ? 'pointer' : 'not-allowed',
            opacity: draft.trim() ? 1 : 0.5,
          }}
        >
          {state.kind === 'loading' ? 'Verifying…' : 'Verify Proof'}
        </button>

        {state.kind === 'ok' && (
          <div
            style={{
              marginTop: 24,
              padding: 20,
              borderRadius: 8,
              border: `1px solid ${state.valid ? '#16a34a' : '#dc2626'}`,
              background: state.valid ? '#052e16' : '#1c0a0a',
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: state.valid ? '#4ade80' : '#f87171' }}>
              {state.valid ? '✓ Signature VALID' : '✗ Signature INVALID'}
            </div>
            <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.8 }}>
              <div><span style={{ color: '#6b7280' }}>Signed by:</span> <code style={{ wordBreak: 'break-all' }}>{state.signedBy}</code></div>
              <div><span style={{ color: '#6b7280' }}>Exists in log:</span> {state.existsInLog ? 'Yes' : 'No'}</div>
              {state.logSeq !== null && (
                <div><span style={{ color: '#6b7280' }}>Log sequence:</span> {state.logSeq}</div>
              )}
            </div>
            {!state.valid && (
              <p style={{ marginTop: 12, color: '#f87171', fontSize: 13 }}>
                The signature does not verify. This entry may have been tampered with or signed by a different key.
              </p>
            )}
          </div>
        )}

        {state.kind === 'error' && (
          <div
            style={{
              marginTop: 24,
              padding: 16,
              borderRadius: 8,
              border: '1px solid #b45309',
              background: '#1c1000',
              color: '#fbbf24',
              fontSize: 13,
            }}
          >
            <strong>Error:</strong> {state.detail}
          </div>
        )}

        <div style={{ marginTop: 48, fontSize: 12, color: '#4b5563', lineHeight: 1.8 }}>
          <p>
            <strong style={{ color: '#6b7280' }}>How to get a proof:</strong>
          </p>
          <code style={{ display: 'block', background: '#111', padding: 12, borderRadius: 6, marginTop: 8 }}>
            curl https://api.growing-intelligence.com/api/v1/proof/42
          </code>
          <p style={{ marginTop: 12 }}>
            <strong style={{ color: '#6b7280' }}>Offline verification (CLI):</strong>
          </p>
          <code style={{ display: 'block', background: '#111', padding: 12, borderRadius: 6, marginTop: 8 }}>
            curl -s …/api/v1/proof/42 | gi-verify -
          </code>
        </div>
      </div>
    </div>
  );
}
